"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { UserCheck, Zap, ShieldCheck, PiggyBank, TrendingUp, Sparkles, CircleCheck as CheckCircle2, CircleX as XCircle, ArrowRight, MessageSquare as MessageSquareCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Consultant() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 30
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const keyBenefits = [{
    id: "benefit-1",
    title: "Direct Access to Me, The Senior SEO Specialist",
    icon: UserCheck,
    blushHighlight: "100% Senior SEO Expertise",
    consultantPerk: "You work directly with me from day one—strategy, audits, and execution are handled entirely by my hands.",
    agencyDrawback: "Agencies pitch with senior staff, then delegate your account to junior executives learning on your budget."
  }, {
    id: "benefit-2",
    title: "Tailored Strategy Built For Your Revenue",
    icon: Sparkles,
    blushHighlight: "Zero Copy-Paste Templates",
    consultantPerk: "I analyze your specific market positioning, technical stack, and business goals to build bespoke SEO roadmaps.",
    agencyDrawback: "Agencies rely on standard monthly checklists and recycled deck templates regardless of your niche."
  }, {
    id: "benefit-3",
    title: "Lean Budget & High Return Efficiency",
    icon: PiggyBank,
    blushHighlight: "100% Focused on Impact",
    consultantPerk: "Your budget goes directly into senior SEO strategy and technical execution—not paying for account managers or flashy agency offices.",
    agencyDrawback: "Up to 50% of your agency fee covers administrative overhead, sales commissions, and middle management."
  }, {
    id: "benefit-4",
    title: "Agile Execution Without Bureaucracy",
    icon: Zap,
    blushHighlight: "Fast & Direct Decisions",
    consultantPerk: "Need technical recommendations or copy updates? I communicate directly with your dev team for rapid turnarounds.",
    agencyDrawback: "Every request passes through account managers, ticketing queues, and multi-tier approval chains."
  }, {
    id: "benefit-5",
    title: "Transparent & Honest Reporting",
    icon: ShieldCheck,
    blushHighlight: "Real Business Value",
    consultantPerk: "I measure success through actual conversions, organic revenue growth, and qualified lead volume—never vanity metrics.",
    agencyDrawback: "Monthly reports filled with jargon and inflated impression stats designed to justify retainer renewals."
  }, {
    id: "benefit-6",
    title: "Personalized Partner Commitment",
    icon: MessageSquareCheck,
    blushHighlight: "Dedicated Attention",
    consultantPerk: "I limit my total client capacity so I can deeply understand your business, industry nuances, and competitive landscape.",
    agencyDrawback: "Account managers balance 15+ clients at once, leading to reactive communications and missed opportunities."
  }];
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative w-full bg-background py-20 lg:py-28 text-foreground overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 reveal-item" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="mb-4 px-3 py-1.5 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-accent/20">
            <Sparkles className="size-3.5 text-accent mr-1 inline" />
            Why Work Directly With Me
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight mb-6">
            The Advantage of an Independent SEO Consultant vs Traditional Agencies
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            When you hire an SEO agency, you often pay for account managers, sales teams, and corporate overhead. 
            When you work with me, you get senior expertise, dedicated focus, and honest communication—directly.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {keyBenefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={benefit.id} data-index={i} className="reveal-item border border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Icon className="size-6" />
                    </div>
                    <Badge variant="outline" className="bg-secondary/60 text-foreground border-accent/30 text-xs font-medium px-2.5 py-0.5">
                      {benefit.blushHighlight}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold font-default leading-snug">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 text-sm leading-relaxed flex-1">
                  {}
                  <div className="p-3.5 rounded-lg bg-secondary/30 border border-accent/20 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-primary text-xs uppercase tracking-wider">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Working With Me</span>
                    </div>
                    <p className="text-foreground/90 font-medium">{benefit.consultantPerk}</p>
                  </div>

                  {}
                  <div className="p-3.5 rounded-lg bg-muted/50 border border-border/60 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                      <XCircle className="size-4 text-destructive/70 shrink-0" />
                      <span>Traditional Agency</span>
                    </div>
                    <p className="text-muted-foreground">{benefit.agencyDrawback}</p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        {}
        <div className="reveal-item rounded-2xl bg-gradient-to-br from-primary/5 via-secondary/40 to-muted border border-accent/20 p-8 md:p-12 shadow-sm max-w-5xl mx-auto mb-16" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-4 max-w-2xl text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-foreground text-xs font-semibold border border-accent/30">
                <TrendingUp className="size-3.5 text-accent" />
                <span>My Personal Commitment</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                No Middlemen. No Junior Hand-Offs. Just Honest SEO That Grows Your Revenue.
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                I limit my client roster so I can personally research, design, and implement your SEO campaign. You get my direct mobile number, clear regular updates, and a strategy tailored specifically to your business goals.
              </p>
            </div>

            <div className="flex-shrink-0 w-full md:w-auto">
              <Button asChild size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base shadow-md">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="text-center reveal-item flex flex-col sm:flex-row items-center justify-center gap-4" style={{
        opacity: 0
      }}>
          <p className="text-sm text-muted-foreground">
            Want to review my proven results first?
          </p>
          <Button asChild variant="outline" size="sm" className="border-border hover:bg-secondary">
            <Link to="/CaseStudiesPage" className="inline-flex items-center gap-1.5">
              <span>Explore My Real Case Studies</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}